/** WHO / SCforH health-enhancing physical activity by age band.
 * Keys align with SCFORH_TARGET_GROUPS. Youth rec is shared by children + adolescents.
 * Club table-tennis minutes contribute toward these totals; they are not TT-only quotas.
 */

export const WHO_PA_BANDS = ["children", "adolescents", "adults", "seniors"];

const YOUTH_BENEFITS = [
  "fitness",
  "obesity",
  "metabolic",
  "bone",
  "depression",
  "selfEsteem",
  "motor",
  "social",
];

const ADULT_BENEFITS = [
  "mortality",
  "chronic",
  "fitness",
  "metabolic",
  "obesity",
  "bone",
  "mental",
];

const SENIOR_BENEFITS = [
  "mortality",
  "chronic",
  "falls",
  "functioning",
  "fitness",
  "metabolic",
  "obesity",
  "bone",
  "cognitive",
  "mental",
  "loneliness",
];

export const WHO_PA_BY_AGE_BAND = {
  children: {
    ageHint: "5–11",
    recKey: "whoRec_youth",
    benefitKeys: YOUTH_BENEFITS,
    dailyMinutes: 60,
    weeklyMinutes: 420,
    vigorousSessionsPerWeek: 3,
    strengthSessionsPerWeek: 3,
    balanceSessionsPerWeek: 0,
  },
  adolescents: {
    ageHint: "12–17",
    recKey: "whoRec_youth",
    benefitKeys: YOUTH_BENEFITS,
    dailyMinutes: 60,
    weeklyMinutes: 420,
    vigorousSessionsPerWeek: 3,
    strengthSessionsPerWeek: 3,
    balanceSessionsPerWeek: 0,
  },
  youth: {
    ageHint: "5–17",
    recKey: "whoRec_youth",
    benefitKeys: YOUTH_BENEFITS,
    dailyMinutes: 60,
    weeklyMinutes: 420,
    vigorousSessionsPerWeek: 3,
    strengthSessionsPerWeek: 3,
    balanceSessionsPerWeek: 0,
  },
  adults: {
    ageHint: "18–64",
    recKey: "whoRec_adults",
    benefitKeys: ADULT_BENEFITS,
    dailyMinutes: 0,
    weeklyMinutes: 150,
    vigorousMinutes: 75,
    vigorousSessionsPerWeek: 0,
    strengthSessionsPerWeek: 2,
    balanceSessionsPerWeek: 0,
  },
  seniors: {
    ageHint: "65+",
    recKey: "whoRec_seniors",
    benefitKeys: SENIOR_BENEFITS,
    dailyMinutes: 0,
    weeklyMinutes: 150,
    vigorousMinutes: 75,
    vigorousSessionsPerWeek: 0,
    strengthSessionsPerWeek: 2,
    balanceSessionsPerWeek: 3,
  },
};

export function ageYearsFromBirthDate(value) {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let years = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) years -= 1;
  return years >= 0 && years < 120 ? years : null;
}

export function resolveWhoBandFromAge(years) {
  if (years == null || !Number.isFinite(Number(years))) return "adults";
  const y = Number(years);
  if (y < 12) return "children";
  if (y < 18) return "adolescents";
  if (y < 65) return "adults";
  return "seniors";
}

export function resolveWhoBandFromBirthDate(value) {
  const years = ageYearsFromBirthDate(value);
  return years == null ? "adults" : resolveWhoBandFromAge(years);
}

/** Collapse children+adolescents into one youth capsule when both are selected. */
export function capsuleBandsForTargetGroups(groups) {
  const set = new Set(Array.isArray(groups) ? groups : []);
  const out = [];
  if (set.has("children") && set.has("adolescents")) out.push("youth");
  else if (set.has("children")) out.push("children");
  else if (set.has("adolescents")) out.push("adolescents");
  if (set.has("adults")) out.push("adults");
  if (set.has("seniors")) out.push("seniors");
  return out;
}

export function getWhoBandMeta(band) {
  return WHO_PA_BY_AGE_BAND[band] || WHO_PA_BY_AGE_BAND.adults;
}

const PROMPT_REC = {
  youth:
    "Children and adolescents (5–17): at least 60 minutes of moderate- to vigorous-intensity activity every day, preferably with at least 3 vigorous sessions each week, including bone- and muscle-strengthening activities.",
  children:
    "Children (5–11): at least 60 minutes of moderate- to vigorous-intensity activity every day, preferably with at least 3 vigorous sessions each week, including bone- and muscle-strengthening activities.",
  adolescents:
    "Adolescents (12–17): at least 60 minutes of moderate- to vigorous-intensity activity every day, preferably with at least 3 vigorous sessions each week, including bone- and muscle-strengthening activities.",
  adults:
    "Adults (18–64): at least 150 minutes of moderate-intensity or 75 minutes of vigorous-intensity aerobic activity each week, plus at least 2 muscle-strengthening sessions involving major muscle groups.",
  seniors:
    "Seniors (65+): at least 150 minutes of moderate-intensity or 75 minutes of vigorous-intensity aerobic activity each week, plus at least 2 muscle-strengthening sessions. Those with poor mobility should also do balance / fall-prevention activity at least 3 times a week.",
};

export function formatWhoGuidelinesForPrompt(targetGroups) {
  const bands = capsuleBandsForTargetGroups(targetGroups);
  if (!bands.length) {
    return [
      "WHO / SCforH physical activity guidelines (frame sessions by age; club table tennis contributes toward these totals, they are not TT-only quotas):",
      `- ${PROMPT_REC.youth}`,
      `- ${PROMPT_REC.adults}`,
      `- ${PROMPT_REC.seniors}`,
    ].join("\n");
  }
  const lines = [
    "WHO / SCforH physical activity guidelines for this club's target groups (frame groups and sessions with these; TT sessions contribute toward the totals):",
  ];
  bands.forEach((band) => {
    lines.push(`- ${PROMPT_REC[band] || PROMPT_REC.adults}`);
  });
  return lines.join("\n");
}

export function formatWhoBandForPrompt(band) {
  const id = WHO_PA_BY_AGE_BAND[band] ? band : "adults";
  return `WHO activity guideline for this user (${id}) — INTERNAL CONTEXT ONLY, do not recite it: ${PROMPT_REC[id] || PROMPT_REC.adults} Club sessions count toward this. Do NOT mention WHO/OMS, quotas, or weekly physical-activity needs in ordinary chat (session open, exercises, weekly volume, auto-eval). Mention it at most ONCE per training cycle, and only at the end of that cycle (cycle wrap-up / last week), in one short sentence. If the user explicitly asks, answer once. Never as a guilt metric.`;
}
